import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, X, Instagram, ArrowRight, ArrowUpRight,
  ShieldCheck, Feather, Eye, Wrench
} from 'lucide-react';
import { products } from '../../mock';
import Official from '../../components/Official';
import Trust from '../../components/Trust';
import Offer from '../../components/Offer';
import Footer from '../../components/Footer';

const CAMPAIGN_SOURCE = 'aficionado';

export const Aficionado = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('campaign_source', CAMPAIGN_SOURCE);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const specs = [
    { icon: ShieldCheck, label: "Protección UV", value: "UV400 certificada", desc: "Filtra el 99% de rayos UVA/UVB. Uso prolongado al sol sin fatiga visual." },
    { icon: Eye, label: "Tratamiento", value: "Antirreflejo", desc: "Capa especial que reduce el deslumbramiento y mejora la claridad visual." },
    { icon: Feather, label: "Peso del marco", value: "Ultraligero", desc: "Material resistente que no molesta aunque las tengas puestas todo el día." },
    { icon: Wrench, label: "Construcción", value: "Premium", desc: "Bisagras firmes, materiales sólidos. Acabados que no se sienten baratos." }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-stone-900/95 backdrop-blur-md border-b border-stone-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_13e5738d-7aa3-4072-b101-9672dafbd61a/artifacts/j65cu3zm_logo.jpg" 
                alt="Gafas La Tri" 
                className="h-12 w-auto object-contain bg-white rounded-md p-1"
                loading="eager"
                fetchpriority="high"
                width="100"
                height="48"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('caracteristicas')} className="text-stone-300 hover:text-amber-400 font-medium text-sm tracking-wide uppercase transition-colors" data-testid="nav-caracteristicas">Características</button>
              <button onClick={() => scrollToSection('comprar')} className="text-stone-300 hover:text-amber-400 font-medium text-sm tracking-wide uppercase transition-colors" data-testid="nav-comprar">Comprar</button>
              <a href="https://www.instagram.com/gafasdelatri" target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-amber-400 transition-colors">
                <Instagram size={18} />
              </a>
              <button 
                onClick={() => scrollToSection('caracteristicas')} 
                data-testid="header-cta-specs"
                className="bg-amber-400 text-stone-900 px-5 py-2.5 rounded-sm font-bold text-sm tracking-wide uppercase hover:bg-amber-300 hover:shadow-lg transition-all flex items-center gap-2"
              >
                Ver especificaciones
                <ArrowUpRight size={16} />
              </button>
            </nav>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white" aria-label="Menu">
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-stone-800">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('caracteristicas')} className="text-left text-stone-300 font-medium py-2 uppercase text-sm tracking-wide">Características</button>
                <button onClick={() => scrollToSection('comprar')} className="text-left text-stone-300 font-medium py-2 uppercase text-sm tracking-wide">Comprar</button>
                <button onClick={() => scrollToSection('comprar')} className="bg-amber-400 text-stone-900 px-5 py-3 rounded-sm font-bold uppercase text-sm">Ver especificaciones</button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        {/* HERO */}
        <section className="relative bg-stone-900 text-white overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-stone-700/40 rounded-full blur-[120px]"></div>
          </div>
          
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }}></div>

          <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-32 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px w-12 bg-amber-400"></div>
                  <span className="text-amber-400 font-bold text-xs tracking-[0.3em] uppercase">Edición Oficial · FEF</span>
                </div>
                
                <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-[0.95] tracking-tight">
                  Diseñadas para
                  <span className="block text-amber-400">quienes sí</span>
                  <span className="block">se fijan en los detalles</span>
                </h1>
                
                <p className="text-lg lg:text-xl text-stone-300 mb-10 leading-relaxed max-w-xl font-light">
                  Gafas oficiales de La Tri con protección real, acabados cuidados y un diseño que se siente premium desde el primer uso.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => scrollToSection('caracteristicas')}
                    data-testid="hero-cta-explorar"
                    className="group bg-amber-400 text-stone-900 px-8 py-4 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-amber-300 hover:shadow-2xl hover:shadow-amber-500/20 transition-all inline-flex items-center justify-center gap-2"
                  >
                    Explorar características
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={() => scrollToSection('comprar')}
                    data-testid="hero-cta-comprar"
                    className="border border-stone-700 hover:border-amber-400 text-white px-8 py-4 rounded-sm font-bold uppercase tracking-wide text-sm transition-all inline-flex items-center justify-center gap-2"
                  >
                    Comprar ahora
                  </button>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-stone-800">
                  <div>
                    <div className="text-3xl font-black text-amber-400 mb-1">UV400</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wider">Protección</div>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-amber-400 mb-1">100%</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wider">Oficial FEF</div>
                  </div>
                  <div>
                    <div className="text-3xl font-black text-amber-400 mb-1">3</div>
                    <div className="text-xs text-stone-400 uppercase tracking-wider">Modelos</div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-tr from-amber-500/20 to-transparent rounded-full blur-3xl"></div>
                <img 
                  src={products[1].image}
                  alt="Gafas La Tri - Modelo Premium"
                  className="relative w-full object-contain"
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            </div>
          </div>

          {/* Bottom marquee */}
          <div className="border-y border-stone-800 py-4 bg-stone-950 overflow-hidden">
            <div className="flex items-center gap-12 whitespace-nowrap animate-pulse">
              <span className="text-stone-500 text-xs font-bold uppercase tracking-[0.3em]">UV400 · Antirreflejo · Ultraligeras · Producto Oficial FEF · UV400 · Antirreflejo · Ultraligeras · Producto Oficial FEF</span>
            </div>
          </div>
        </section>

        {/* PRODUCTO OFICIAL CON LICENCIA */}
        <Official />

        {/* OFFER - Merged section: color selection + pack offer */}
        <Offer
          colorHeading="Tres personalidades, una misma calidad"
          headingTitle="Una para ti,"
          headingTitleAccent="otra para compartir"
          headingSubtitle="Llévate dos pares con precio de lanzamiento y comparte la misma calidad con quien tú elijas."
        />

        {/* ESPECIFICACIONES - Lo que hace diferentes a estas gafas */}
        <section id="caracteristicas" className="py-24 bg-stone-900 text-white relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-3xl mb-16">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-amber-400 font-bold text-xs tracking-[0.3em] uppercase">Especificaciones</span>
                <div className="h-px w-12 bg-amber-400"></div>
              </div>
              <h2 className="text-4xl lg:text-6xl font-black mb-6 leading-tight tracking-tight">
                Lo que hace diferentes
                <span className="block text-amber-400">a estas gafas</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {specs.map((spec, idx) => (
                <div 
                  key={idx}
                  data-testid={`spec-card-${idx}`}
                  className="group bg-stone-800/50 backdrop-blur-sm border border-stone-700 hover:border-amber-400 rounded-sm p-8 transition-all"
                >
                  <div className="flex items-start gap-5">
                    <div className="bg-amber-400 text-stone-900 w-14 h-14 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <spec.icon size={26} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-amber-400 font-bold uppercase tracking-wider mb-1">{spec.label}</div>
                      <div className="text-2xl font-black mb-3">{spec.value}</div>
                      <p className="text-stone-400 leading-relaxed font-light">{spec.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MATERIALES Y ACABADOS - Acabados pensados para durar */}
        <section className="py-24 bg-stone-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -inset-4 bg-stone-900/5 rounded-sm -rotate-2"></div>
                  <img 
                    src={products[2].gallery ? products[2].gallery[1] : products[2].image}
                    alt="Detalle de acabados de Gafas La Tri"
                    className="relative w-full object-cover aspect-[4/5] bg-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-amber-600 font-bold text-xs tracking-[0.3em] uppercase">Construcción</span>
                  <div className="h-px w-12 bg-amber-600"></div>
                </div>
                <h2 className="text-4xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
                  Acabados pensados
                  <span className="block text-stone-400">para durar</span>
                </h2>
                <p className="text-lg text-stone-600 mb-10 font-light leading-relaxed">
                  Combina la estética de La Tri con una construcción sólida: marcos resistentes, bisagras firmes y detalles de color que no se sienten baratos.
                </p>
                <div className="space-y-5">
                  {[
                    { label: "Marcos", value: "Polímero resistente" },
                    { label: "Bisagras", value: "Metálicas firmes" },
                    { label: "Detalles de color", value: "Acabado mate premium" },
                    { label: "Lentes", value: "Tratamiento antirreflejo" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-3 border-b border-stone-200">
                      <span className="text-stone-500 uppercase text-xs tracking-wider font-bold">{item.label}</span>
                      <span className="text-stone-900 font-bold">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST - LAS GRANDES TIENDAS - Respaldo que puedes ver en el mundo real */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="h-px w-12 bg-amber-600"></div>
                <span className="text-amber-600 font-bold text-xs tracking-[0.3em] uppercase">Retail Presence</span>
                <div className="h-px w-12 bg-amber-600"></div>
              </div>
              <h2 className="text-4xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
                Respaldo que puedes ver
                <span className="block text-stone-400">en el mundo real</span>
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed mb-4 font-light max-w-3xl mx-auto">
                Las Gafas La Tri se encuentran en puntos de venta seleccionados de Supermaxi, Megamaxi, AKÍ, Marathon Sports e islas Panini. Esa presencia en cadenas reconocidas respalda la calidad y consistencia del producto.
              </p>
              <p className="text-stone-500 italic font-light">
                Lo que estás evaluando aquí es el mismo estándar que ya pasó por la vitrina de las principales cadenas del país.
              </p>
            </div>
          </div>
          <Trust hideHeader={true} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Aficionado;
